// src/com/arrive_charge_info/ArriveChargeInfo.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import {
    Button, IconButton, CircularProgress, DialogContent, DialogActions, Chip
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PaidIcon from '@mui/icons-material/Paid'; // 운임정보 Popup.
import DoneIcon from '@mui/icons-material/Done'; // Save.
import UploadFileIcon from '@mui/icons-material/UploadFile'; // CSV Upload.
import FindInPageIcon from '@mui/icons-material/FindInPage'; // search file
import ClearIcon from '@mui/icons-material/Clear'; // Cancel.
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, arriveChargeInfoListRepo, vehTypeInfoListRepo
} from '../../value/AppModel';
import {
    LIST_ARRIVE_CHARGE_INFO, SET_USAGE_ARRIVE_CHARGE_INFO, UPLOAD_ARRIVE_CHARGE_INFO,
    ADD_ARRIVE_CHARGE_INFO, EDIT_ARRIVE_CHARGE_INFO, REMOVE_ARRIVE_CHARGE_INFO
} from './ArriveChargeInfoGql';
import { ArriveChargeInfoDictionary } from './ArriveChargeInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable, { sumColumnWidth } from '../../common/flexytable/FlexyTable';
import DraggableEditor from '../../common/DraggableEditor';
import { LIST_VEH_TYPE_INFO } from '../veh_type_info/VehTypeInfoGql';
import VzmSelector from '../../common/VzmSelector';
import InputUtil from '../../value/InputUtil';
import VzmIntField from '../../common/VzmIntField';
import { usePapaParse } from 'react-papaparse';


const InputMap = ArriveChargeInfoDictionary.inputMap;

/**
 * (ARRIVE_CHARGE_INFO) 정보 View Controller
 * DC에서 도착지까지 운임정보
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-04-30 17:21:50 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function ArriveChargeInfo({
    arriveInfoGiven,
    open,
    onRequestClose,
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [openUploader, setOpenUploader] = useState(false);
    const [doneUpload, setDoneUpload] = useState(false);
    const [editorState, setEditorState] = useState({open:false, isEdit:false}); // in-line editor.
    const arriveChargeInfoListData = useReactiveVar(arriveChargeInfoListRepo);
    const vehTypeInfoListData = useReactiveVar(vehTypeInfoListRepo);
    const [csvDataList, setCsvDataList] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const checkAuth = useAuthorizationCheck();
    const arriveInfo = useMemo(() =>{
        if(arriveInfoGiven) return arriveInfoGiven;
        return {};
    }, [arriveInfoGiven]);
    const vehTypeList = useMemo(() => {
        if(open) {
            const isFreshDc = arriveInfo.dcTypeCd === 'F';
            const list = vehTypeInfoListData.records.filter(item => {
                if(isFreshDc) return item.freshCargo;
                else return !item.freshCargo;
            });
            return list;
        }
        return [];
    }, [vehTypeInfoListData, open, arriveInfo]);
    const canEdit = useMemo(() => {
        return checkAuth(authTableTree.tree.pr_arrive_charge_info_edit);
    }, [authTableTree, checkAuth]);
    const [useEucKr, setUseEucKr] = useState(false);
    const { readString } = usePapaParse();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const fileInputRef = useRef(null); // for file input click
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getVehTypeInfoList, responseVehTypeList] = useLazyQuery(LIST_VEH_TYPE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeList(data, option)},
        onError: (error) => addAlert({title: '차종목록 조회 오류', message: [error.message]})
    });
    const [getArriveChargeInfoList, responseList] = useLazyQuery(LIST_ARRIVE_CHARGE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: ArriveChargeInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at con_sole (Uncaught exception) ###
    const [addArriveChargeInfo, responseAdd] = useMutation( ADD_ARRIVE_CHARGE_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: ArriveChargeInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editArriveChargeInfo, responseEdit] = useMutation( EDIT_ARRIVE_CHARGE_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: ArriveChargeInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeArriveChargeInfo, responseRemove] = useMutation( REMOVE_ARRIVE_CHARGE_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: ArriveChargeInfoDictionary.errorTitle.Remove, message: [error.message]})
	});
    const [setUsageArriveChargeInfo, responseSetUsage] = useMutation( SET_USAGE_ARRIVE_CHARGE_INFO, {
        onCompleted: (data, option) => onComplateSetUsage(data, option),
        onError: (error) => addAlert({title: '운임정보 사용여부 변경 오류', message: [error.message]})
    });
    const [uploadArriveChargeInfo, responseUpload] = useMutation( UPLOAD_ARRIVE_CHARGE_INFO, {
        onCompleted: (data, option) => onCompletedUpload(data, option),
        onError: (error) => addAlert({title: '운임정보 업로드 오류', message: [error.message]})
    });

    const getArriveChargeInfoListNow = () => {
        const param = {variables:{arriveId:arriveInfoGiven.arriveId}};
        getArriveChargeInfoList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    const getVehTypeInfoListOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - vehTypeInfoListData.when < 600 && vehTypeInfoListData.records.length > 0) return;
        getVehTypeInfoList();
    };

    useEffect(() => {
        if(open) {
            setEditorState(AppObject.EmptyViewerState);
            setOpenUploader(false);
            setDoneUpload(false);
            getVehTypeInfoListOnCondition();
            //const diff = new Date().getTime()/1000 - arriveChargeInfoListData.when;
            // if(diff > 300) //  || arriveChargeInfoListData.records.length === 0 - Don't use this condition
            getArriveChargeInfoListNow();
        }
    }, [open]);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.arriveChargeInfoList) {
            arriveChargeInfoListRepo({when:new Date().getTime()/1000, records:data.arriveChargeInfoList});
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.arriveChargeInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            enqueueSnackbar('운임정보가 등록되었습니다.', {variant:'success', autoHideDuration:1500});
            getArriveChargeInfoListNow();
        }
        else addAlert({title:ArriveChargeInfoDictionary.errorTitle.Add, message:[data.arriveChargeInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.arriveChargeInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            enqueueSnackbar('운임정보가 수정되었습니다.', {variant:'success', autoHideDuration:1500});
            getArriveChargeInfoListNow();
        }
        else addAlert({title:ArriveChargeInfoDictionary.errorTitle.Edit, message:[data.arriveChargeInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.arriveChargeInfoRemove.ok) {
            getArriveChargeInfoListNow();
            enqueueSnackbar('운임정보가 삭제되었습니다.', {variant:'success', autoHideDuration:1500});
        }
        else addAlert({title:ArriveChargeInfoDictionary.errorTitle.Remove, message:[data.arriveChargeInfoRemove.message]});
    };

    const onComplateSetUsage = (data, option) => {
        if(data.arriveChargeInfoSetUsage.ok) {
            getArriveChargeInfoListNow();
            enqueueSnackbar('운임 사용여부가 변경되었습니다.', {variant:'success', autoHideDuration:1500});
        }
        else addAlert({title: '운임 사용여부 변경 오류', message:[data.arriveChargeInfoSetUsage.message]});
    };
    
    const onCompleteGetVehTypeList = (data, clientOption) => {
        if(data.vehTypeInfoList) {
            vehTypeInfoListRepo({when:new Date().getTime()/1000, records:data.vehTypeInfoList});
        }
    };

    const onCompletedUpload = (data, option) => {
        if(data.arriveChargeInfoUpload.ok) {
            // reflect the result to the table.(ok, message, list {ok, message, arriveId, vehTypeId})
            const newTableData = [];
            let errorCount = 0;
            for(const prev of csvDataList) {
                const found = data.arriveChargeInfoUpload.list.find((item)=>prev.vehTypeId === item.vehTypeId);
                if(found) {
                    // prev.ok = found.message === 'ok';
                    prev.message = found.message;
                    newTableData.push(prev);
                    if(found.message !== 'ok') errorCount++;
                }
                else newTableData.push(prev);
            }
            setCsvDataList(newTableData);
            setDoneUpload(true);
            enqueueSnackbar(`운임정보 업로드 완료${errorCount > 0 ? ` (${errorCount}건 오류)` : ''}`, {variant: errorCount > 0 ? 'warning' : 'success', autoHideDuration: 2500});
        }
        else addAlert({title: '운임정보 업로드', message:[data.arriveChargeInfoUpload.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getArriveChargeInfoListNow();
    };

    const onClickSave = () => {
        const mutateParam = {variables:{arriveChargeInfo:input.data}};
        if(editorState.isEdit) editArriveChargeInfo(mutateParam);
        else addArriveChargeInfo(mutateParam);
    };

    const onClickCancel = () => {
        setInput(AppObject.emptyInputData);
        setEditorState({open:false, isEdit:false});
    };

    const onClickAdd = () => {
        const newInput = {...AppObject.emptyInputData};
        newInput.data = {
            arriveId: arriveInfo.arriveId,
            vehTypeId: '',
            charge: 0,
            addCharge: 0,
            regTypeCd: 'M',
        };
        setInput(newInput);
        setEditorState({open:true, isEdit:false});
    };

    const onCsvFileRead = (text, isEucKr) => {
        // parse using papaparse.
        setUseEucKr(isEucKr);
        readString(text, {
            worker: true,
            complete: (result) => {
                const { data, errors, meta } = result;
                const theDc = arriveInfo.dcCd;
                // const theStore = arriveInfo.storeId;
                const records
                = data.filter((row) => /^\d{3}/.test(row[0]))
                .map((row) => {
                    const newRow = row.slice(0, 4); // 기존 4개 컬럼: 출발지코드, 도착지코드(점포), 차종코드, 운임비
                    const errors = [];
                    // const arrIdInput = '' + newRow[0] + newRow[1]; // dc + store
                    if(theDc !== newRow[0]) {
                        errors.push('출발지코드 오류');
                    }
                    if(!/^\d+$/.test(newRow[1])) {
                        errors.push('도착지코드 오류');
                    }

                    /* if(theStore !== newRow[1]) {
                        errors.push('도착지코드 오류');
                    } */

                    if(!/^\d{2}$/.test(newRow[2])) {
                        errors.push('차종코드 오류(2자리 숫자)');
                    }
                    const chargeVal = InputUtil.csvNumber(newRow[3]) || 0;
                    if(isNaN(chargeVal) || chargeVal < 0 || chargeVal > 999999999) {
                        errors.push('운임오류(0~999,999,999)');
                    }
                    return {
                        dcCd: newRow[0],
                        storeId: newRow[1],
                        arriveId: '' + newRow[0] + newRow[1], // dc + store (concat)
                        vehTypeId: newRow[2],
                        charge: chargeVal,
                        message: errors.length > 0 ? errors.join(', ') : 'ok',
                        // addCharge: 0,
                        // regTypeCd: 'A',
                    };
                });
                setCsvDataList(records);
            }
        });
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                InputUtil.readTextFileWithAutoEncoding(file, onCsvFileRead);
            }
        }
    };

    const onClickOpenUploader = () => {
        setCsvDataList([]);
        setUseEucKr(false);
        setOpenUploader(true);
        setDoneUpload(false);
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onRequestEdit = (item) => {
        const newInput = {...AppObject.emptyInputData};
        newInput.data = {
            arriveId: item.arriveId,
            vehTypeId: item.vehTypeId,
            charge: item.charge,
            addCharge: item.addCharge,
            regTypeCd: 'M',
        };
        setInput(newInput);
        setEditorState({open:true, isEdit:true});
    };

    const onClickUpload = () => {
        // $arriveChargeInfo: [InputArriveChargeInfoEdit]!
        const param = {
            variables: {
                arriveChargeInfo: csvDataList.filter(item => item.message === 'ok').map(item => ({
                    arriveId: item.arriveId,
                    vehTypeId: item.vehTypeId,
                    charge: item.charge,
                    addCharge: item.addCharge || 0,
                    regTypeCd: 'A',
                }))
            }
        };
        uploadArriveChargeInfo(param);
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운임정보 삭제",
            content: [
                `운임정보를 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeArriveChargeInfo({variables:{arriveChargeInfo:{arriveId:item.arriveId, vehTypeId:item.vehTypeId}}});
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운임 데이터 사용중지",
            content: [
                `${item.vehTypeName} 차종의 운임 데이터를 사용중지하시겠습니까?`,
            ]
        });

        if(confirm)
            setUsageArriveChargeInfo({
                variables:{
                    arriveChargeInfo:{
                        arriveId:item.arriveId,
                        vehTypeId:item.vehTypeId,
                        useYn:'N'
                    }}}
            );
    };

    const confirmReusing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운임 데이터 사용",
            content: [
                `${item.vehTypeName} 차종의 운임 데이터를 사용하도록 변경하시겠습니까?`,
            ]
        });
        if(confirm) setUsageArriveChargeInfo({
            variables:{arriveChargeInfo:{arriveId:item.arriveId, vehTypeId:item.vehTypeId, useYn:'Y'}}
        });
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        if(canEdit) onRequestEdit(record);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            case AppObject.command.REUSE: confirmReusing(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_arrive_charge_info_add);
    const canUpload = checkAuth(authTableTree.tree.pr_arrive_charge_info_save);
    // const canEdit = checkAuth(authTableTree.tree.pr_arrive_charge_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_arrive_charge_info_remove);
    const canSetUsage = checkAuth(authTableTree.tree.pr_arrive_charge_info_use);
    const tools = canEdit ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>},
        // {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ] : [];

    if(canSetUsage) {
        tools.push({
            id: AppObject.command.STOP_USING, icon: <StopCircleIcon/>,
            color: AppStyle.mui.error,
            where: (row)=>row.useYn === 'Y'
        });
        tools.push({
            id: AppObject.command.REUSE, icon: <PlayCircleFilledWhiteIcon/>,
            color: AppStyle.mui.success,
            where: (row)=>row.useYn === 'N'
        });
    }

    if(canRemove) {
        tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});
    }

    const renderListHeader = (loadingAny) => {
        return(
            <ContentHeader>
                <ControlBox style={{paddingLeft:10}}>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <Button
                            color={AppStyle.mui.primary}
                            variant={AppStyle.mui.contained}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            disabled={loadingAny}
                            startIcon={<NoteAddIcon />}
                        >
                            운임추가
                        </Button>
                    }
                    {
                        canUpload &&
                        <Button
                            color={AppStyle.mui.secondary}
                            variant={AppStyle.mui.contained}
                            style={{marginLeft:10}}
                            onClick={onClickOpenUploader}
                            disabled={loadingAny}
                            startIcon={<UploadFileIcon />}
                        >
                            CSV 업로드
                        </Button>
                    }
                </ToolBox>
            </ContentHeader>
        );
    };
    
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, editorState.isEdit);
        setInput(newInput);
    };

    const renderEditor = (loadingAny) => {
        return(
            <ContentHeader>
                <ControlBox style={{paddingLeft:10}}>
                    <VzmSelector
                        dict={InputMap.vehTypeId}
                        value={input.data.vehTypeId}
                        onChange={onChangeValue}
                        list={vehTypeList}
                        disabled={loadingAny || editorState.isEdit}
                    />
                    <VzmIntField
                        dict={InputMap.charge}
                        value={input.data.charge}
                        onChange={onChangeValue}
                        disabled={loadingAny}
                        style={{marginLeft:10}}
                    />
                    <Button
                        variant={AppStyle.mui.contained}
                        disabled={loadingAny || input.hasError || !input.changed}
                        color={AppStyle.mui.primary}
                        startIcon={<DoneIcon />}
                        onClick={onClickSave}
                        style={{marginLeft:10}}
                    >저장</Button>
                    <Button
                        variant={AppStyle.mui.contained}
                        disabled={loadingAny}
                        color={AppStyle.mui.warning}
                        startIcon={<ClearIcon />}
                        onClick={onClickCancel}
                        style={{marginLeft:10}}
                    >취소</Button>
                </ControlBox>
            </ContentHeader>
        )
    };

    const renderUploaderHeader = () => {
        return(
            <ContentHeader>
                <ControlBox style={{paddingLeft:10, justifyContent:'end'}}>
                    {
                        /*
                        <VzmCheckbox
                            dict={{
                                id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                            }}
                            value={useEucKr}
                            onChange={(id,value,err) => setUseEucKr(value)}
                        />
                        */
                        fileInputRef.current && canUpload &&
                        <>
                        {
                            useEucKr ?
                            <Chip label="완성형한글로 읽음" color={AppStyle.mui.warning}
                                size={AppWord.SMALL}
                            />
                            :
                            csvDataList.length > 0 ?
                            <Chip label="UTF-8로 읽음" color={AppStyle.mui.success}
                                size={AppWord.SMALL}
                            /> : null
                        }
                        <Button
                            color={AppStyle.mui.secondary}
                            style={{marginLeft:10}}
                            onClick={onClickFindFile}
                            startIcon={<FindInPageIcon />}
                            variant={AppStyle.mui.contained}
                            disabled={loadingAny || doneUpload}
                        >
                            파일 선택
                        </Button>
                        </>
                    }
                    {
                        doneUpload ?
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            onClick={() => {
                                setOpenUploader(false);
                                setDoneUpload(false);
                                getArriveChargeInfoListNow();
                            }}
                            style={{marginLeft:10}}
                        >닫기</Button>
                        :
                        <Button
                            variant={AppStyle.mui.contained}
                            disabled={loadingAny}
                            color={AppStyle.mui.warning}
                            startIcon={<ClearIcon />}
                            onClick={() => {
                                setOpenUploader(false);
                                setDoneUpload(false);
                            }}
                            style={{marginLeft:10}}
                        >취소</Button>}
                </ControlBox>
            </ContentHeader>
        );
    };

    const loadingAny
        = responseList.loading
        || responseAdd.loading
        || responseEdit.loading
        || responseRemove.loading
        || responseVehTypeList.loading
        || responseSetUsage.loading
        || responseUpload.loading;

    if(!open || !Boolean(arriveInfo.arriveId)) return null;

    const tableWidth = sumColumnWidth(ArriveChargeInfoDictionary.tableProps.columns);
    const ContentStyle = {
        position: 'relative', minHeight:'50vh', minWidth:tableWidth + 100, width:'50vw',
        overflowY: 'hidden', padding:0,
    };
    const BoxStyle = {
        position:'absolute', left:0, top:0, right:0, bottom:3, 
        display:'flex', flexDirection: 'column'
    };

    return(
        <DraggableEditor open={open && Boolean(arriveInfo.arriveId)} maxWidth='lg'
            onClose={onRequestClose}
            startIcon={<PaidIcon fontSize={AppWord.LARGE} />}
            title={`차종별 운임 - 출발지: [${arriveInfo.dcName}] 도착지: [${arriveInfo.storeName}]`}
        >
            {
                openUploader ?
                <DialogContent
                    style={ContentStyle}
                >
                    <Box style={BoxStyle}>
                        {renderUploaderHeader()}
                        <FlexyTable
                            name={ArriveChargeInfoDictionary.uploadTableProps.tableId}
                            tableProps={ArriveChargeInfoDictionary.uploadTableProps}
                            records={csvDataList}
                            hideBubble={true}
                        />
                    </Box>
                </DialogContent>
                :
                <DialogContent
                    style={ContentStyle}
                >
                    <Box style={BoxStyle}>
                        {
                            editorState.open ?
                            renderEditor(loadingAny) :
                            renderListHeader(loadingAny)
                        }
                        <FlexyTable
                            name={ArriveChargeInfoDictionary.tableProps.tableId}
                            tableProps={ArriveChargeInfoDictionary.tableProps}
                            records={arriveChargeInfoListData.records}
                            tools={tools}
                            onClickTool={onClickToolOnRow}
                            onRowDoubleClicked={onDoubleClickRow}
                            onChangeCountToDisplay={setCountToDisplay}
                            hideBubble={true}
                        />
                    </Box>
                </DialogContent>}
            <DialogActions>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onChooseFile}
                />
                {
                    openUploader && csvDataList.length > 0 && !doneUpload &&
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        disabled={
                            loadingAny || csvDataList.filter(item => item.message === 'ok').length === 0
                        }
                        onClick={onClickUpload}
                    >{
                        csvDataList.filter(item => item.message === 'ok').length > 0
                        ?
                        `${csvDataList.filter(item => item.message === 'ok').length}개 업로드`
                        :
                        '모든 레코드 오류'
                    }</Button>

                }
                <Button
                    onClick={onRequestClose}
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.inherit}
                    disabled={loadingAny || editorState.open}
                >닫기</Button>
            </DialogActions>
        </DraggableEditor>
    );
}

/*

예전 CSV 파일 업로드 방식: Backend Service에서 처리했음.
    예전코드: 출발지코드	도착지코드(점포)	차종코드	운임비
    Java: String[] columns = new String[] { "dc_cd", "store_id", "veh_type_cd", "charge"  };

새로운 시스템은 같은 CSV 파일을 사용할 수 있도록 하고, react-papaparse 사용하여 파싱.
ArriveInfoUploader.js 참조.

*/
